import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Select,
  MenuItem,
  TextField,
  IconButton,
  CircularProgress,
  Typography,
  Chip,
  Box,
  RadioGroup,
  FormControlLabel,
  Radio
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PercentIcon from '@mui/icons-material/Percent';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import { toast } from 'react-toastify';
import { useAuth } from '../../AuthContext';
import configData from '../../configure';

const PRESET_TITLES = [
  'Merit Scholarship',
  'Sibling Concession',
  'Staff Ward Discount',
  'Financial Aid / EWS',
  'Sports Scholarship',
  'Management Concession',
  'Early Bird Rebate',
  'Custom Concession'
];

export default function FeeConcessionModal({
  open,
  onClose,
  selectedStudent,
  baseFeeStructure,
  existingProfile,
  selectedClassId,
  academicYear,
  onSaveSuccess
}) {
  const { axios: authAxios, token, user } = useAuth();
  const orgId = user?.orgId || '62d3b1f4-e453-4933-bfcf-7b5adf5b4e83';

  const [saving, setSaving] = useState(false);
  const [termOverrides, setTermOverrides] = useState({});

  // Initialize overrides from base structure and existing profile
  useEffect(() => {
    if (!open || !baseFeeStructure) return;

    const installments = baseFeeStructure.feeInstallments || baseFeeStructure.installments || [];
    const existingOverridesMap = {};

    if (existingProfile && existingProfile.studentFeeOverrides) {
      existingProfile.studentFeeOverrides.forEach(ov => {
        if (ov.baseInstallmentId) {
          const discount = (ov.discounts && ov.discounts[0]) || {};
          existingOverridesMap[ov.baseInstallmentId] = {
            title: discount.title || 'Concession',
            discountType: discount.discountType || 'FLAT',
            value: discount.value !== undefined && discount.value !== null ? discount.value : 0,
            remarks: discount.remarks || ''
          };
        }
      });
    }

    const initialMap = {};
    installments.forEach(inst => {
      const instId = inst.installmentId || inst.id;
      if (instId) {
        initialMap[instId] = existingOverridesMap[instId] || {
          title: 'Merit Scholarship',
          discountType: 'FLAT',
          value: 0,
          remarks: ''
        };
      }
    });

    setTermOverrides(initialMap);
  }, [open, baseFeeStructure, existingProfile]);

  // Calculate live totals
  const calculationSummary = useMemo(() => {
    if (!baseFeeStructure) return { baseTotal: 0, totalDiscount: 0, netTotal: 0 };

    const installments = baseFeeStructure.feeInstallments || baseFeeStructure.installments || [];
    let baseTotal = 0;
    let totalDiscount = 0;

    const termCalculations = installments.map(inst => {
      const instId = inst.installmentId || inst.id;
      const baseFee = Number(inst.totalFee || inst.amount || 0);
      baseTotal += baseFee;

      const override = termOverrides[instId] || { title: '', discountType: 'FLAT', value: 0, remarks: '' };
      let discountAmount = 0;
      const val = Number(override.value) || 0;

      if (override.discountType === 'PERCENTAGE') {
        discountAmount = (baseFee * Math.min(100, Math.max(0, val))) / 100;
      } else {
        discountAmount = Math.min(baseFee, Math.max(0, val));
      }

      totalDiscount += discountAmount;
      const netPayable = Math.max(0, baseFee - discountAmount);

      return {
        installmentId: instId,
        description: inst.description,
        baseFee,
        discountAmount,
        netPayable,
        override
      };
    });

    const netTotal = Math.max(0, baseTotal - totalDiscount);

    return {
      baseTotal,
      totalDiscount,
      netTotal,
      termCalculations
    };
  }, [baseFeeStructure, termOverrides]);

  const handleOverrideChange = (instId, field, val) => {
    setTermOverrides(prev => ({
      ...prev,
      [instId]: {
        ...prev[instId],
        [field]: val
      }
    }));
  };

  const handleSave = async () => {
    if (!selectedStudent || !baseFeeStructure) {
      toast.error('Student and Fee Structure details are required.');
      return;
    }

    try {
      setSaving(true);

      const installments = baseFeeStructure.feeInstallments || baseFeeStructure.installments || [];
      const overridesPayload = calculationSummary.termCalculations.map(tc => {
        const inst = installments.find(i => (i.installmentId || i.id) === tc.installmentId);
        const baseBreakdown = inst?.feeBreakdown || {};

        return {
          baseInstallmentId: tc.installmentId,
          customFeeBreakdown: baseBreakdown,
          netPayableFee: tc.netPayable,
          discounts: tc.override.value > 0 ? [
            {
              title: tc.override.title || 'Concession',
              discountType: tc.override.discountType,
              value: Number(tc.override.value),
              amount: tc.discountAmount,
              remarks: tc.override.remarks || ''
            }
          ] : []
        };
      });

      const payload = {
        studentId: selectedStudent.userId || selectedStudent.id || selectedStudent.studentId,
        feeStructureId: baseFeeStructure.feeStructureId || baseFeeStructure.id,
        classId: selectedClassId || baseFeeStructure.classId,
        academicYear: academicYear || '2025-2026',
        orgId,
        netTotalFee: calculationSummary.netTotal,
        studentFeeOverrides: overridesPayload
      };

      let url, method;
      if (existingProfile && existingProfile.studentFeeProfileId) {
        url = `${configData.STUDENT_FEE_PROFILE}/${existingProfile.studentFeeProfileId}`;
        method = authAxios.put;
      } else {
        url = configData.STUDENT_FEE_PROFILE;
        method = authAxios.post;
      }

      await method(url, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      toast.success(`Fee concession saved successfully for ${selectedStudent.displayName || selectedStudent.firstName || 'student'}.`);
      if (onSaveSuccess) onSaveSuccess();
      onClose();
    } catch (err) {
      console.error('Failed to save student fee concession:', err);
      const errMsg = err.response?.data?.message || err.response?.data?.error || 'Failed to save fee concession.';
      toast.error(errMsg);
    } finally {
      setSaving(false);
    }
  };

  const studentName = selectedStudent?.displayName || `${selectedStudent?.firstName || ''} ${selectedStudent?.lastName || ''}`.trim() || 'Selected Student';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth className="fcm-dialog">
      <DialogTitle className="fcm-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <LocalOfferIcon />
            <h2 className="fcm-title">Student Fee Concession</h2>
          </div>
        </div>
        <IconButton onClick={onClose} style={{ color: '#ffffff' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent style={{ padding: '24px', backgroundColor: '#f8fafc' }}>
        {/* Student & Overall Summary Card */}
        <Box
          style={{
            background: '#ffffff',
            borderRadius: '14px',
            padding: '18px 20px',
            border: '1px solid #e2e8f0',
            marginBottom: '24px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '16px'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <AccountCircleIcon style={{ fontSize: '42px', color: '#014486' }} />
            <div>
              <Typography variant="h6" style={{ fontWeight: 700, fontSize: '16px', color: '#0f172a' }}>
                {studentName}
              </Typography>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
            <div style={{ textAlign: 'right' }}>
              <span style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', fontWeight: 600 }}>Base Class Fee</span>
              <div style={{ fontSize: '16px', fontWeight: 700, color: '#334155' }}>
                ₹{calculationSummary.baseTotal.toLocaleString('en-IN')}
              </div>
            </div>

            <div style={{ textAlign: 'right' }}>
              <span style={{ fontSize: '11px', color: '#047857', textTransform: 'uppercase', fontWeight: 600 }}>Total Concession</span>
              <div style={{ fontSize: '16px', fontWeight: 700, color: '#047857' }}>
                - ₹{calculationSummary.totalDiscount.toLocaleString('en-IN')}
              </div>
            </div>

            <div style={{ textAlign: 'right', background: '#eff6ff', padding: '8px 14px', borderRadius: '10px', border: '1px solid #bfdbfe' }}>
              <span style={{ fontSize: '11px', color: '#014486', textTransform: 'uppercase', fontWeight: 700 }}>Net Payable</span>
              <div style={{ fontSize: '18px', fontWeight: 800, color: '#014486' }}>
                ₹{calculationSummary.netTotal.toLocaleString('en-IN')}
              </div>
            </div>
          </div>
        </Box>

        {/* Per-Installment Concession Form */}
        <Typography variant="subtitle1" style={{ fontWeight: 700, color: '#1e293b', marginBottom: '14px' }}>
          Term / Installment Concession Breakdown
        </Typography>

        {calculationSummary.termCalculations.map((tc) => {
          const ov = tc.override;
          const hasDiscount = tc.discountAmount > 0;

          return (
            <div key={tc.installmentId} className={`fcm-term-card ${hasDiscount ? 'has-discount' : ''}`}>
              <div className="fcm-term-header">
                <div>
                  <span className="fcm-term-title">{tc.description}</span>
                  <span className="fcm-term-base" style={{ marginLeft: '12px' }}>
                    Base Term Fee: ₹{tc.baseFee.toLocaleString('en-IN')}
                  </span>
                </div>
                {hasDiscount && (
                  <Chip
                    label={`Saved ₹${tc.discountAmount.toLocaleString('en-IN')} (${tc.netPayable === 0 ? 'Full Waiver' : `Net: ₹${tc.netPayable.toLocaleString('en-IN')}`})`}
                    size="small"
                    style={{ background: '#dcfce7', color: '#15803d', fontWeight: 700 }}
                  />
                )}
              </div>

              <div className="fcm-term-inputs">
                {/* Discount Category / Title */}
                <div>
                  <label style={{ fontSize: '12px', fontWeight: 600, color: '#475569', display: 'block', marginBottom: '4px' }}>
                    Concession Title / Category
                  </label>
                  <Select
                    size="small"
                    fullWidth
                    value={ov.title || 'Merit Scholarship'}
                    onChange={(e) => handleOverrideChange(tc.installmentId, 'title', e.target.value)}
                  >
                    {PRESET_TITLES.map(title => (
                      <MenuItem key={title} value={title}>{title}</MenuItem>
                    ))}
                  </Select>
                </div>

                {/* Discount Type */}
                <div>
                  <label style={{ fontSize: '12px', fontWeight: 600, color: '#475569', display: 'block', marginBottom: '4px' }}>
                    Discount Type
                  </label>
                  <Select
                    size="small"
                    fullWidth
                    value={ov.discountType || 'FLAT'}
                    onChange={(e) => handleOverrideChange(tc.installmentId, 'discountType', e.target.value)}
                  >
                    <MenuItem value="FLAT">Flat (₹)</MenuItem>
                    <MenuItem value="PERCENTAGE">Percentage (%)</MenuItem>
                  </Select>
                </div>

                {/* Discount Value */}
                <div>
                  <label style={{ fontSize: '12px', fontWeight: 600, color: '#475569', display: 'block', marginBottom: '4px' }}>
                    {ov.discountType === 'PERCENTAGE' ? 'Discount (%)' : 'Discount Amount (₹)'}
                  </label>
                  <TextField
                    size="small"
                    type="number"
                    fullWidth
                    placeholder="0"
                    value={ov.value || ''}
                    onChange={(e) => handleOverrideChange(tc.installmentId, 'value', e.target.value)}
                    inputProps={{ min: 0, max: ov.discountType === 'PERCENTAGE' ? 100 : tc.baseFee }}
                  />
                </div>

                {/* Net Term Result */}
                <div>
                  <label style={{ fontSize: '12px', fontWeight: 600, color: '#475569', display: 'block', marginBottom: '4px' }}>
                    Net Term Fee
                  </label>
                  <div
                    style={{
                      height: '40px',
                      borderRadius: '4px',
                      background: '#f1f5f9',
                      display: 'flex',
                      alignItems: 'center',
                      padding: '0 12px',
                      fontWeight: 700,
                      color: tc.netPayable < tc.baseFee ? '#047857' : '#1e293b',
                      border: '1px solid #cbd5e1'
                    }}
                  >
                    ₹{tc.netPayable.toLocaleString('en-IN')}
                  </div>
                </div>
              </div>

              {/* Remarks / Description */}
              <div>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Remarks / Description (e.g. Granted by Principal for Sports Excellence)..."
                  value={ov.remarks || ''}
                  onChange={(e) => handleOverrideChange(tc.installmentId, 'remarks', e.target.value)}
                />
              </div>
            </div>
          );
        })}
      </DialogContent>

      <DialogActions style={{ padding: '16px 24px', background: '#ffffff', borderTop: '1px solid #e2e8f0' }}>
        <Button onClick={onClose} disabled={saving} style={{ color: '#64748b' }}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving}
          variant="contained"
          style={{ background: '#014486', color: '#ffffff', fontWeight: 700, padding: '8px 24px', borderRadius: '10px' }}
        >
          {saving ? <CircularProgress size={22} color="inherit" /> : 'Save Concession Override'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
